import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useScrollOverflow } from '@/hooks/useScrollOverflow';
import type { SnippetLanguage } from '@/utils/mcpClients';

interface CodeSnippetProps {
    code: string;
    /** Short caption above the block, also used in the copy button name. */
    label?: string;
    language?: SnippetLanguage;
    /** Overrides the accessible name of the copy button. */
    ariaLabel?: string;
    className?: string;
    testId?: string;
}

/**
 * Monospace block with a copy button.
 * The block scrolls horizontally on its own so the page never scrolls sideways.
 */
export function CodeSnippet({
    code,
    label,
    language,
    ariaLabel,
    className = '',
    testId,
}: CodeSnippetProps) {
    const { copy, isCopied } = useCopyToClipboard('Copied to clipboard');
    const { scrollRef, canScrollEnd } = useScrollOverflow<HTMLPreElement>();

    // The name changes with the state, so a screen reader hears the copy succeed.
    const copyLabel = ariaLabel
        ? isCopied
            ? `${ariaLabel} (copied)`
            : ariaLabel
        : `${isCopied ? 'Copied' : 'Copy'} ${label ?? 'snippet'}`;

    return (
        <div className={`min-w-0 ${className}`}>
            <div className="mb-1 flex items-center justify-between gap-2">
                {label ? (
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {label}
                    </span>
                ) : (
                    <span />
                )}
                <button
                    type="button"
                    onClick={() => void copy(code)}
                    aria-label={copyLabel}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-dark"
                >
                    {isCopied ? (
                        <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    ) : (
                        <ClipboardDocumentIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    {isCopied ? 'Copied' : 'Copy'}
                </button>
            </div>
            <pre
                ref={scrollRef}
                data-testid={testId}
                data-language={language}
                className={`overflow-x-auto rounded-md border border-gray-200 bg-gray-50 p-3 [font-variant-ligatures:none] dark:border-gray-700 dark:bg-dark ${
                    canScrollEnd ? 'scroll-fade-end' : ''
                }`}
            >
                <code className="whitespace-pre font-mono text-sm text-gray-800 dark:text-gray-200">
                    {code}
                </code>
            </pre>
        </div>
    );
}
